/** SUNTRONIX ONE — Apple India Catalogue Sync.
 *  TECHNOLOGY → PRODUCT CONTENT → APPLE INDIA SYNC.
 *  Governed auto-discovery + India OEM part-number enrichment of Product Master.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  RefreshCw, Sparkles, Apple, PackageCheck, AlertTriangle, ShieldCheck,
  History, Send, Loader2, IndianRupee, Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const INR = (v) =>
  v == null ? "—" : "₹" + Number(v).toLocaleString("en-IN");

const QR_STYLES = {
  QUOTE_READY: "bg-emerald-100 text-emerald-800 border-emerald-200",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-200",
  ENRICHING: "bg-sky-100 text-sky-800 border-sky-200",
  UNAVAILABLE_INDIA: "bg-slate-100 text-slate-600 border-slate-200",
  DISCONTINUED: "bg-rose-100 text-rose-700 border-rose-200",
};

function Stat({ icon: Icon, label, value, tone, testId }) {
  return (
    <div data-testid={testId}
      className="rounded-xl border border-slate-200 bg-white px-4 py-3.5 flex items-center gap-3">
      <div className={"h-10 w-10 rounded-lg grid place-items-center shrink-0 " + (tone || "bg-slate-100 text-slate-600")}>
        <Icon className="h-5 w-5" strokeWidth={1.6} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold text-slate-900 leading-none">{value}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function AppleIndiaSync() {
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [rows, setRows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [alert, setAlert] = useState(null);
  const [ask, setAsk] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAns, setAiAns] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/apple-india/overview"),
      api.get("/apple-india/products", { params: { q: q || undefined, quote_readiness: filter || undefined, page_size: 100 } }),
      api.get("/apple-india/history"),
    ]).then(([o, p, h]) => {
      setOv(o.data); setRows(p.data.items || []); setRuns(h.data.items || []);
    }).catch(() => toast.error("Failed to load Apple India catalogue"))
      .finally(() => setLoading(false));
  }, [q, filter]);

  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    setSyncing(true); setAlert(null);
    try {
      const { data } = await api.post("/apple-india/sync/run", { mode: "full", seed_if_empty: true });
      setAlert(data);
      toast.success(`Apple India catalogue updated — ${data.created} new, ${data.updated} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const runAsk = async () => {
    if (!ask.trim()) return;
    setAiBusy(true); setAiAns("");
    try {
      const { data } = await api.post("/apple-india/ai/ask", { question: ask.trim() });
      setAiAns(data.answer || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assistant unavailable");
    } finally { setAiBusy(false); }
  };

  const createQuote = (p) =>
    navigate(`/commercial/crm/quotations?new=1&product=${encodeURIComponent(p.id)}&part=${encodeURIComponent(p.oem_part_number || "")}`);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="apple-india-sync">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-slate-900 text-white grid place-items-center">
            <Apple className="h-6 w-6" strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Apple India Catalogue Sync</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Automatic new-product detection &amp; India OEM part-number enrichment · Market: INDIA · Nightly 02:00 &amp; 07:00 IST
            </p>
          </div>
        </div>
        <Button onClick={runSync} disabled={syncing} data-testid="apple-run-refresh"
          className="bg-slate-900 hover:bg-slate-800 text-white">
          {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {syncing ? "Refreshing…" : "Run Full Refresh"}
        </Button>
      </div>

      {/* Governed source note */}
      <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-4 py-2.5 text-[12px] text-sky-900 flex items-start gap-2" data-testid="apple-source-note">
        <Apple className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.6} />
        <span>
          Products are enriched from the official <b>apple.com/in</b> India store snapshot (Priority 3 source) plus the approved curated catalogue.
          Real India OEM part numbers (…HN/A) and MRP are imported as-is — never guessed.
          New launches whose part number is only confirmed inside the buy page are staged as <b>Review Required · part pending</b> until confirmed.
        </span>
      </div>

      {/* Sales alert */}
      {alert && (
        <div data-testid="apple-sync-alert"
          className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex flex-wrap gap-x-6 gap-y-1">
          <span className="font-semibold">Apple India catalogue updated</span>
          <span>New Products: <b>{alert.created}</b></span>
          <span>Quote Ready: <b>{alert.quote_ready}</b></span>
          <span>Review Required: <b>{alert.review_required}</b></span>
          <span>Price Changes: <b>{alert.price_changes}</b></span>
        </div>
      )}

      {/* Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat testId="stat-total" icon={PackageCheck} label="Apple India SKUs" value={ov?.total_products ?? "—"} tone="bg-slate-900 text-white" />
        <Stat testId="stat-ready" icon={ShieldCheck} label="Quote Ready" value={ov?.quote_ready ?? "—"} tone="bg-emerald-100 text-emerald-700" />
        <Stat testId="stat-review" icon={AlertTriangle} label="Review Required" value={ov?.review_required ?? "—"} tone="bg-amber-100 text-amber-700" />
        <Stat testId="stat-new" icon={Sparkles} label="New Launches" value={ov?.new_launch ?? "—"} tone="bg-sky-100 text-sky-700" />
        <Stat testId="stat-hsn" icon={IndianRupee} label="Missing / Review HSN" value={ov?.missing_hsn ?? "—"} tone="bg-rose-100 text-rose-700" />
      </div>

      {/* AI assistant */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-semibold text-slate-800">Apple Launch Assistant</span>
          <span className="text-[11px] text-slate-400">ONE AI · answers only from your data</span>
        </div>
        <div className="flex gap-2">
          <Input data-testid="apple-ai-input" value={ask} onChange={(e) => setAsk(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runAsk()}
            placeholder="e.g. Which Apple products are not quote ready? Show MacBook Air India configurations." />
          <Button onClick={runAsk} disabled={aiBusy} data-testid="apple-ai-send" variant="secondary">
            {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        {aiAns && (
          <div data-testid="apple-ai-answer"
            className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 whitespace-pre-wrap">
            {aiAns}
          </div>
        )}
      </div>

      {/* Filters + New Products table */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-100">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="apple-search" value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9" placeholder="Search name, OEM part #, chip, memory, storage…" />
          </div>
          {["", "QUOTE_READY", "REVIEW_REQUIRED"].map((f) => (
            <button key={f || "all"} onClick={() => setFilter(f)}
              data-testid={`apple-filter-${f || "all"}`}
              className={"text-xs px-3 py-1.5 rounded-full border transition-colors " +
                (filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
              {f ? f.replace("_", " ") : "All"}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-[10px] uppercase text-slate-500 text-left">
                <th className="px-3 py-2.5">Product</th>
                <th className="px-3 py-2.5 hidden lg:table-cell">Configuration</th>
                <th className="px-3 py-2.5">India Part #</th>
                <th className="px-3 py-2.5 hidden md:table-cell">MRP</th>
                <th className="px-3 py-2.5 hidden lg:table-cell">HSN / GST</th>
                <th className="px-3 py-2.5">Quote Ready</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">
                  No Apple India products yet. Run a Full Refresh to discover the current lineup.</td></tr>
              )}
              {rows.map((p) => (
                <tr key={p.id} data-testid={`apple-row-${p.oem_part_number}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-slate-800 flex items-center gap-2">
                      {p.product_name}
                      {p.is_new_launch && (
                        <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px]">NEW</Badge>
                      )}
                      {p.config_type === "CTO" && (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">CTO</Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400">{p.product_family} · {p.category}</div>
                    {p.source_provenance === "APPLE_IN_STORE" && (
                      p.source_url ? (
                        <a href={p.source_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-sky-700 hover:underline mt-0.5">
                          <Apple className="h-3 w-3" /> apple.com/in
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-sky-700 mt-0.5">
                          <Apple className="h-3 w-3" /> apple.com/in
                        </span>
                      )
                    )}
                  </td>
                  <td className="px-3 py-2.5 hidden lg:table-cell text-slate-500 text-xs">
                    {[p.chip, p.memory, p.storage].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-700">
                    {p.oem_part_number
                      ? p.oem_part_number
                      : <span className="text-amber-600 font-sans not-italic">Pending · buy page</span>}
                  </td>
                  <td className="px-3 py-2.5 hidden md:table-cell text-slate-700">{INR(p.mrp)}</td>
                  <td className="px-3 py-2.5 hidden lg:table-cell text-xs text-slate-600">
                    {p.hsn_sac || "—"} <span className="text-slate-400">/ {p.gst_percent ?? "—"}%</span>
                    {p.hsn_status && <span className="text-[10px] text-slate-400 block">{p.hsn_status}</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge className={"text-[10px] border " + (QR_STYLES[p.quote_readiness] || QR_STYLES.ENRICHING)}>
                      {(p.quote_readiness || "ENRICHING").replace("_", " ")}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {p.quote_readiness === "QUOTE_READY" && (
                      <Button size="sm" variant="ghost" data-testid={`apple-quote-${p.oem_part_number}`}
                        onClick={() => createQuote(p)} className="text-xs text-slate-700 hover:text-slate-900">
                        Create Quote
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sync History */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 p-3 border-b border-slate-100">
          <History className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-800">Sync History</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-[10px] uppercase text-slate-500 text-left">
                <th className="px-3 py-2.5">Run</th>
                <th className="px-3 py-2.5">When</th>
                <th className="px-3 py-2.5 hidden md:table-cell">Trigger</th>
                <th className="px-3 py-2.5">Checked</th>
                <th className="px-3 py-2.5">Created</th>
                <th className="px-3 py-2.5">Updated</th>
                <th className="px-3 py-2.5 hidden md:table-cell">Price Δ</th>
                <th className="px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {runs.map((r) => (
                <tr key={r.id} data-testid={`apple-run-${r.id}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{new Date(r.run_at).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2.5 hidden md:table-cell text-xs">{r.trigger}</td>
                  <td className="px-3 py-2.5">{r.checked}</td>
                  <td className="px-3 py-2.5 text-emerald-700">{r.created}</td>
                  <td className="px-3 py-2.5">{r.updated}</td>
                  <td className="px-3 py-2.5 hidden md:table-cell">{r.price_changes}</td>
                  <td className="px-3 py-2.5">
                    <Badge className={"text-[10px] " + (r.status === "PASS"
                      ? "bg-emerald-100 text-emerald-700" : r.status === "FAIL"
                      ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700")}>
                      {r.status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No sync runs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
